/**
 * MemoForm's Save/Cancel footer — Cancel/Save buttons + the ⌘ Enter hint.
 * Rendered only by the non-inline (modal/compose) variants; the inline
 * (record-slideout) variant autosaves and has no explicit Save/Cancel, so its
 * caller (MemoForm) simply doesn't render this component rather than this
 * component gating on `isInline` itself.
 */

import { Button } from '@/lib/base/buttons/button';
import { isMacPlatform } from '@/lib/utils/platform';

export interface MemoFormFooterProps {
  submitLabel: string;
  isValid: boolean;
  isSubmitting: boolean;
  onCancel: () => void;
}

export function MemoFormFooter({
  submitLabel,
  isValid,
  isSubmitting,
  onCancel,
}: MemoFormFooterProps) {
  // MemoForm accepts both ⌘/Ctrl+Enter (see handleKeyDown: `metaKey || ctrlKey`),
  // so show the modifier that matches the user's platform — ⌘ on mac, Ctrl
  // elsewhere — mirroring SearchLauncher's ⌘K/Ctrl+K hint.
  const saveHint = isMacPlatform() ? '⌘ Enter to save' : 'Ctrl Enter to save';
  return (
    <div className='flex items-center justify-between px-3 py-2 border-t border-[var(--color-border-secondary)]'>
      <span className='text-[10px] md:text-xs text-fg-quaternary'>
        {saveHint}
      </span>
      <div className='flex items-center gap-3'>
        <Button
          type='button'
          color='tertiary'
          size='sm'
          onClick={onCancel}
          isDisabled={isSubmitting}
          disabledTooltip='Saving…'
        >
          Cancel
        </Button>
        <Button
          type='submit'
          color='primary'
          size='sm'
          isDisabled={!isValid || isSubmitting}
          disabledTooltip={
            isSubmitting ? 'Saving…' : 'Add a note or title to save'
          }
          isLoading={isSubmitting}
        >
          {/* Create: "Add note" / "Add task", following the form's entity type
            (see MemoForm's submitLabel derivation). Edit: "Save". */}
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}
